import React, { useEffect, useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  PawPrint,
  Printer,
  Download,
  Stethoscope,
  Search,
  AlertCircle,
  FileText,
  Weight,
  Sparkles,
  BookOpen,
  Pill,
  Wand2,
} from "lucide-react";

import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { Toaster } from "./ui/sonner";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Menu } from "lucide-react";

import {
  SPECIES,
  getDiseasesForSpecies,
  getDiseaseById,
  getAllDiseases,
  setBackendDiseases,
} from "../data/prescriptions";

import RxPad from "./RxPad";
import RxEditor from "./RxEditor";
import DiseaseLibraryEditor from "./DiseaseLibraryEditor";
import DrugLibraryEditor from "./DrugLibraryEditor";
import ConsultPage from "./ConsultPage";

const todayISO = () => {
  const d = new Date();
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

export default function PetsmateApp() {
  const handleLogout = () => {
    localStorage.removeItem("vetprescribe_token");
    localStorage.removeItem("vetprescribe_user");
    window.location.reload();
  };
const [diseasesLoaded, setDiseasesLoaded] = useState(false);

useEffect(() => {
  let cancelled = false;

  const loadDiseases = async () => {
    try {
      const response = await fetch("/api/diseases?active=true");

      if (!response.ok) {
        throw new Error(`Disease API failed: ${response.status}`);
      }

      const data = await response.json();

      if (!cancelled && Array.isArray(data) && data.length > 0) {
        setBackendDiseases(data);
        setDiseasesLoaded(true);
      }
    } catch (error) {
      console.error("Failed to load diseases from backend:", error);
    }
  };

  loadDiseases();

  return () => {
    cancelled = true;
  };
}, []);
  const [view, setView] = useState("rx"); // "rx" | "library" | "drugs" | "consult"
  const [species, setSpecies] = useState("canine");
  const [weight, setWeight] = useState("");
  const [diseaseId, setDiseaseId] = useState("");
  const [vetName, setVetName] = useState("Dr. ");
  const [patientName, setPatientName] = useState("");
  const [generated, setGenerated] = useState(null);
  const [editableMeds, setEditableMeds] = useState([]);
  const [editableCare, setEditableCare] = useState([]);
  const [editableDx, setEditableDx] = useState([]);
  const [editableDdx, setEditableDdx] = useState([]);
  const [isExporting, setIsExporting] = useState(false);

  const rxRef = useRef(null);

  const diseaseOptions = useMemo(
  () => getDiseasesForSpecies(species),
  [species, view, diseasesLoaded]
);

const totalCount = useMemo(
  () => getAllDiseases().length,
  [view, diseasesLoaded]
);

  const handleSpeciesChange = (val) => {
    setSpecies(val);
    setDiseaseId("");
    setGenerated(null);
    setEditableMeds([]);
    setEditableCare([]);
    setEditableDx([]);
    setEditableDdx([]);
  };

  const handleGenerate = () => {
    if (!species) return toast.error("Select species first.");
    const w = Number(weight);
    if (!w || w <= 0 || w > 100) {
      return toast.error("Enter a valid body weight (0.1–100 kg).");
    }
    if (!diseaseId) return toast.error("Select a diagnosis.");
    const d = getDiseaseById(diseaseId);
    setGenerated({
      disease: d,
      species,
      weight: w,
      vetName: vetName.trim() || "Attending Vet",
      patientName: patientName.trim() || "—",
      date: todayISO(),
    });
    setEditableMeds((d.medications || []).map((m) => ({ ...m })));
    setEditableCare([...(d.supportiveCare || [])]);
    setEditableDx((d.diagnostics || []).map((t) => ({ ...t })));
    setEditableDdx([...(d.differentials || [])]);
    toast.success("Prescription generated", { description: `${d.name} · ${w} kg` });
  };

  const handleReset = () => {
    setWeight("");
    setDiseaseId("");
    setPatientName("");
    setGenerated(null);
    setEditableMeds([]);
    setEditableCare([]);
    setEditableDx([]);
    setEditableDdx([]);
  };

  /** Called by the AI Consult page — pre-fills the Rx pad with the AI draft. */
  const applyAiToRx = (payload) => {
    if (!payload) return;
    const {
      disease,
      species: sp,
      weight: w,
      patientName: pn,
      includeMeds = true,
      includeDx = true,
      includeDdx = true,
      includeCare = true,
      filterField = null,
      filterIndex = null,
    } = payload;

    // Ensure Rx state has a "generated" case
    let baseGenerated = generated;
    const same = baseGenerated && baseGenerated.disease?.id === disease.id;
    if (!same) {
      baseGenerated = {
        disease,
        species: sp,
        weight: w,
        vetName: vetName.trim() || "Attending Vet",
        patientName: pn || "—",
        date: todayISO(),
      };
      setGenerated(baseGenerated);
      // Also reflect the species / weight on the Rx form
      setSpecies(sp);
      setWeight(String(w));
      setPatientName(pn || "");
      // Start with empty editable buckets — we'll add filtered items below
      setEditableMeds([]);
      setEditableDx([]);
      setEditableDdx([]);
      setEditableCare([]);
    }

    // Merge helpers avoid duplicates by shallow equality
    const mergeMeds = (curr, incoming) => {
      const names = new Set(curr.map((m) => (m.drug || "").toLowerCase()));
      return [...curr, ...incoming.filter((m) => !names.has((m.drug || "").toLowerCase()))];
    };
    const mergeStrings = (curr, incoming) => {
      const set = new Set(curr.map((s) => (s || "").toLowerCase()));
      return [...curr, ...incoming.filter((s) => !set.has((s || "").toLowerCase()))];
    };
    const mergeDx = (curr, incoming) => {
      const key = (t) => `${(t.test || "").toLowerCase()}|${(t.finding || "").toLowerCase()}`;
      const set = new Set(curr.map(key));
      return [...curr, ...incoming.filter((t) => !set.has(key(t)))];
    };

    // Convert AI's snake_case medication → app camelCase used by Rx pad
    const normaliseMed = (m) => ({
      drug: m.drug || "",
      doseMgPerKg: m.dose_mg_per_kg ?? m.doseMgPerKg ?? null,
      doseText: m.dose_text ?? m.doseText ?? "",
      route: m.route || "PO",
      frequency: m.frequency || "SID",
      duration: m.duration || "",
      notes: m.notes || "",
    });

    const filterOne = (arr, field, wantField) =>
      filterField === wantField && filterIndex != null && arr[filterIndex] != null
        ? [arr[filterIndex]]
        : arr;

    const meds = includeMeds
      ? filterOne(disease.medications || [], "med", "med").map(normaliseMed)
      : [];
    const dx = includeDx ? filterOne(disease.diagnostics || [], "dx", "dx") : [];
    const ddx = includeDdx ? filterOne(disease.differentials || [], "ddx", "ddx") : [];
    const care = includeCare ? filterOne(disease.supportiveCare || [], "care", "care") : [];

    if (meds.length) setEditableMeds((prev) => mergeMeds(prev, meds));
    if (dx.length) setEditableDx((prev) => mergeDx(prev, dx));
    if (ddx.length) setEditableDdx((prev) => mergeStrings(prev, ddx));
    if (care.length) setEditableCare((prev) => mergeStrings(prev, care));
  };

  const handlePrint = useReactToPrint({
    contentRef: rxRef,
    documentTitle: `Petsmate-Rx-${generated?.disease?.id || "prescription"}`,
    onAfterPrint: () => toast.success("Sent to printer"),
  });
const handleDownloadPDF = async () => {
  if (!rxRef.current || !generated) return;

  try {
    setIsExporting(true);

    const node = rxRef.current;

    // Wait for fonts
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }

    // Wait for images inside the prescription
    const images = Array.from(node.querySelectorAll("img"));

    await Promise.all(
      images.map(
        (img) =>
          new Promise((resolve) => {
            if (img.complete) {
              resolve();
            } else {
              img.onload = resolve;
              img.onerror = resolve;
            }
          })
      )
    );

    // Give React/browser one moment to finish rendering
    await new Promise((resolve) => setTimeout(resolve, 300));

    const canvas = await html2canvas(node, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      backgroundColor: "#ffffff",
      logging: false,
      imageTimeout: 15000,
      windowWidth: node.scrollWidth,
      windowHeight: node.scrollHeight,
      scrollX: 0,
      scrollY: 0,
      onclone: (clonedDoc) => {
        const clonedNode = clonedDoc.querySelector('[data-testid="rx-pad"]');

        if (clonedNode) {
          clonedNode.style.display = "block";
          clonedNode.style.visibility = "visible";
          clonedNode.style.opacity = "1";
          clonedNode.style.backgroundColor = "#ffffff";
          clonedNode.style.width = "820px";
          clonedNode.style.maxWidth = "820px";
          clonedNode.style.margin = "0";
        }
      },
    });

    if (!canvas.width || !canvas.height) {
      throw new Error("PDF capture produced an empty canvas");
    }

    const imgData = canvas.toDataURL("image/jpeg", 0.95);

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "a4",
      compress: true,
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const margin = 20;
    const imgWidth = pageWidth - margin * 2;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = margin;

    pdf.addImage(
      imgData,
      "JPEG",
      margin,
      position,
      imgWidth,
      imgHeight,
      undefined,
      "FAST"
    );

    heightLeft -= pageHeight - margin * 2;

    while (heightLeft > 0) {
      position = margin - (imgHeight - heightLeft);

      pdf.addPage();

      pdf.addImage(
        imgData,
        "JPEG",
        margin,
        position,
        imgWidth,
        imgHeight,
        undefined,
        "FAST"
      );

      heightLeft -= pageHeight - margin * 2;
    }

    pdf.save(
      `Petsmate-Rx-${generated.disease.id}-${Date.now()}.pdf`
    );

    toast.success("PDF downloaded");
  } catch (e) {
    console.error("PDF export error:", e);
    toast.error("Failed to export PDF");
  } finally {
    setIsExporting(false);
  }
};
   return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#1A1F1D]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <Toaster richColors position="top-center" />

      {view === "library" ? (
        <DiseaseLibraryEditor onBack={() => setView("rx")} />
      ) : view === "drugs" ? (
        <DrugLibraryEditor onBack={() => setView("rx")} />
      ) : view === "consult" ? (
        <ConsultPage
          onBack={() => setView("rx")}
          onApplyToRx={(payload, opts = {}) => {
            applyAiToRx(payload);
            if (opts.navigate) setView("rx");
          }}
        />
      ) : (
        <>
      {/* Header */}
      <header className="no-print border-b border-[#E2E8F0] bg-white/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-[#0F4C3A] flex items-center justify-center text-white">
              <PawPrint className="w-5 h-5" />
            </div>
            <div className="leading-none">
              <div className="text-lg tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 700 }}>
                Petsmate
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C6B66] mt-1">
                Rx Assistant · Clinical Tool
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
           
            <div className="hidden md:flex items-center gap-2 text-xs text-[#5C6B66] mr-1">
              <span className="w-2 h-2 rounded-full bg-[#0F4C3A]" />
              Formulary v2.0 · {totalCount} conditions
            </div>

            <Button
              variant="outline"
              onClick={handleLogout}
              className="rounded-md border-[#E2E8F0] hover:border-[#0F4C3A] hover:text-[#0F4C3A]"
             >
              Logout
             </Button>
            <Button
              data-testid="open-consult"
              onClick={() => setView("consult")}
              className="rounded-md bg-[#0F4C3A] hover:bg-[#166B52] text-white px-3 sm:px-4"
            >
              <Wand2 className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">AI Consult</span>
            </Button>
            {/* Desktop: show secondary buttons inline */}
            <Button
              data-testid="open-drugs"
              variant="outline"
              onClick={() => setView("drugs")}
              className="hidden md:inline-flex rounded-md border-[#0F4C3A]/40 text-[#0F4C3A] hover:bg-[#0F4C3A] hover:text-white"
            >
              <Pill className="w-4 h-4 mr-1.5" /> Drug Formulary
            </Button>
            <Button
              data-testid="open-library"
              variant="outline"
              onClick={() => setView("library")}
              className="hidden md:inline-flex rounded-md border-[#0F4C3A]/40 text-[#0F4C3A] hover:bg-[#0F4C3A] hover:text-white"
            >
              <BookOpen className="w-4 h-4 mr-1.5" /> Disease Library
            </Button>
            {/* Mobile: fold secondary nav into a menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="open-mobile-menu"
                  variant="outline"
                  className="md:hidden rounded-md border-[#E2E8F0] text-[#1A1F1D]"
                  aria-label="Open menu"
                >
                  <Menu className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  data-testid="mobile-open-drugs"
                  onClick={() => setView("drugs")}
                  className="cursor-pointer"
                >
                  <Pill className="w-4 h-4 mr-2 text-[#0F4C3A]" /> Drug Formulary
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="mobile-open-library"
                  onClick={() => setView("library")}
                  className="cursor-pointer"
                >
                  <BookOpen className="w-4 h-4 mr-2 text-[#0F4C3A]" /> Disease Library
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="mobile-open-consult"
                  onClick={() => setView("consult")}
                  className="cursor-pointer"
                >
                  <Wand2 className="w-4 h-4 mr-2 text-[#0F4C3A]" /> AI Consult
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="no-print max-w-[1400px] mx-auto px-6 md:px-10 pt-12 pb-6">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#0F4C3A] bg-[#0F4C3A]/5 px-3 py-1.5 rounded-full">
              <Sparkles className="w-3 h-3" />
              Instant Canine & Feline Prescriptions
            </div>
            <h1
              className="mt-4 text-4xl sm:text-5xl tracking-tight text-[#1A1F1D]"
              style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 500 }}
            >
              Weight in.<br />
              <span className="text-[#0F4C3A]">Prescription out.</span>
            </h1>
            <p className="mt-4 text-[#5C6B66] leading-relaxed max-w-xl">
              A clinical shortcut for the Petsmate team. Choose species, enter body weight, pick a diagnosis
              — the app auto-calculates doses, prints a signed Rx pad, and exports to PDF.
            </p>
          </div>
          <div className="text-xs text-[#5C6B66] hidden lg:block">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-3.5 h-3.5" /> Print-ready A4 layout
            </div>
            <div className="flex items-center gap-2 mb-1">
              <Weight className="w-3.5 h-3.5" /> Auto mg/kg calculation
            </div>
            <div className="flex items-center gap-2">
              <Stethoscope className="w-3.5 h-3.5" /> Formulary-based (editable)
            </div>
          </div>
        </div>
      </section>

      {/* Workspace */}
      <main className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Input panel */}
          <aside className="no-print lg:col-span-4">
            <Card className="border-[#E2E8F0] shadow-sm rounded-xl">
              <CardContent className="p-6 space-y-5">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C6B66] font-semibold">
                    Case Details
                  </div>
                  <div
                    className="text-xl mt-1 tracking-tight"
                    style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 500 }}
                  >
                    New Consultation
                  </div>
                </div>

                <div>
                  <Label htmlFor="patient-name" className="text-[11px] uppercase tracking-[0.1em] text-[#5C6B66] font-semibold">
                    Patient Name <span className="normal-case text-[#5C6B66] font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="patient-name"
                    data-testid="patient-name-input"
                    value={patientName}
                    onChange={(e) => setPatientName(e.target.value)}
                    placeholder="e.g. Bruno / Whiskers"
                    className="mt-2 border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
                  />
                </div>

                <div>
                  <Label className="text-[11px] uppercase tracking-[0.1em] text-[#5C6B66] font-semibold">Species</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {SPECIES.map((s) => (
                      <button
                        key={s.value}
                        type="button"
                        data-testid={`species-${s.value}`}
                        onClick={() => handleSpeciesChange(s.value)}
                        className={`rounded-md border px-4 py-3 text-sm text-left transition-colors duration-150 ${
                          species === s.value
                            ? "border-[#0F4C3A] bg-[#0F4C3A] text-white"
                            : "border-[#E2E8F0] bg-white text-[#1A1F1D] hover:border-[#0F4C3A]/60"
                        }`}
                      >
                        <div className="font-medium">{s.label.split(" ")[0]}</div>
                        <div className={`text-[11px] mt-0.5 ${species === s.value ? "text-white/70" : "text-[#5C6B66]"}`}>
                          {s.value === "canine" ? "Dog" : "Cat"}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="weight-input" className="text-[11px] uppercase tracking-[0.1em] text-[#5C6B66] font-semibold">
                    Body Weight (kg)
                  </Label>
                  <Input
                    id="weight-input"
                    data-testid="weight-input"
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="100"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    placeholder="e.g. 12.5"
                    className="mt-2 border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
                  />
                </div>

                <div>
                  <Label className="text-[11px] uppercase tracking-[0.1em] text-[#5C6B66] font-semibold">Diagnosis</Label>
                  <Select value={diseaseId} onValueChange={setDiseaseId}>
                    <SelectTrigger
                      data-testid="disease-selector"
                      className="mt-2 border-[#E2E8F0] focus:ring-[#0F4C3A]"
                    >
                      <SelectValue placeholder="Select condition…" />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      {diseaseOptions.map((d) => (
                        <SelectItem key={d.id} value={d.id} data-testid={`disease-option-${d.id}`}>
                          <div className="flex flex-col">
                            <span>{d.name}</span>
                            <span className="text-[10px] uppercase tracking-[0.1em] text-[#5C6B66]">
                              {d.category}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="vet-name" className="text-[11px] uppercase tracking-[0.1em] text-[#5C6B66] font-semibold">
                    Prescribing Vet
                  </Label>
                  <Input
                    id="vet-name"
                    data-testid="vet-name-input"
                    value={vetName}
                    onChange={(e) => setVetName(e.target.value)}
                    placeholder="Dr. Priya Sharma"
                    className="mt-2 border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
                  />
                </div>

                <Separator className="bg-[#E2E8F0]" />

                <div className="flex flex-col gap-2">
                  <Button
                    data-testid="generate-rx-button"
                    onClick={handleGenerate}
                    className="w-full h-11 rounded-md bg-[#0F4C3A] hover:bg-[#166B52] text-white transition-transform duration-150 hover:-translate-y-[1px]"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    Generate Prescription
                  </Button>
                  <Button
                    data-testid="reset-button"
                    variant="outline"
                    onClick={handleReset}
                    className="w-full h-10 rounded-md border-[#E2E8F0] hover:border-[#0F4C3A] hover:text-[#0F4C3A]"
                  >
                    Clear
                  </Button>
                </div>
              </CardContent>
            </Card>
            </aside>

          {/* Preview panel */}
          <section className="lg:col-span-8">
            {generated ? (
              <div className="space-y-4">
                <div className="no-print flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-[#0F4C3A] hover:bg-[#0F4C3A] text-white rounded-full">
                      Draft Rx · Awaiting signature
                    </Badge>
                    <span className="text-xs text-[#5C6B66]" data-testid="rx-summary">
                      {generated.disease.name} · {generated.species === "canine" ? "Canine" : "Feline"} · {generated.weight} kg
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      data-testid="print-button"
                      variant="outline"
                      onClick={handlePrint}
                      className="rounded-md border-[#E2E8F0] hover:border-[#0F4C3A] hover:text-[#0F4C3A]"
                    >
                      <Printer className="w-4 h-4 mr-2" /> Print
                    </Button>
                    <Button
                      data-testid="download-pdf-button"
                      onClick={handleDownloadPDF}
                      disabled={isExporting}
                      className="rounded-md bg-[#0F4C3A] hover:bg-[#166B52] text-white transition-transform duration-150 hover:-translate-y-[1px]"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {isExporting ? "Exporting…" : "Download PDF"}
                    </Button>
                  </div>
                </div>

                <div className="print-container">
                  <RxPad
                    ref={rxRef}
                    disease={generated.disease}
                    species={generated.species}
                    weight={generated.weight}
                    vetName={generated.vetName}
                    patientName={generated.patientName}
                    date={generated.date}
                    medications={editableMeds}
                    supportiveCare={editableCare}
                    diagnostics={editableDx}
                    differentials={editableDdx}
                  />
                </div>

                <RxEditor
                  medications={editableMeds}
                  onChange={setEditableMeds}
                  supportiveCare={editableCare}
                  onCareChange={setEditableCare}
                  diagnostics={editableDx}
                  onDiagnosticsChange={setEditableDx}
                  differentials={editableDdx}
                  onDifferentialsChange={setEditableDdx}
                  weight={generated.weight}
                />
              </div>
            ) : (
              <EmptyState />
            )}
            </section>
         </div>
      </main>
        </>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <Card className="no-print border-dashed border-[#E2E8F0] bg-white rounded-xl">
      <CardContent className="p-10 flex flex-col items-center justify-center text-center">
        <div className="relative w-full max-w-md aspect-[5/4] rounded-lg overflow-hidden mb-6">
          <img
            src="https://images.pexels.com/photos/16395150/pexels-photo-16395150.jpeg"
            alt="Dog and cat waiting"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-white via-white/40 to-transparent" />
        </div>
        <div className="flex items-center gap-2 text-[#5C6B66] text-xs uppercase tracking-[0.18em]">
          <AlertCircle className="w-3.5 h-3.5" />
          No Rx yet
        </div>
        <div
          className="mt-2 text-2xl tracking-tight text-[#1A1F1D]"
          style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 500 }}
        >
          Fill the case details to generate a prescription
        </div>
        <p className="mt-2 text-sm text-[#5C6B66] max-w-md leading-relaxed">
          Doses are auto-calculated from the patient&apos;s body weight against the Petsmate formulary. You can
          edit the vet name and export a signed PDF once generated.
        </p>
      </CardContent>
    </Card>
  );
}
